import { FadeUp, PageHero, Chapter } from "../components/Reveal";
import { CONTACT, IMG, TEAM } from "../lib/content";
import Triskelion from "../components/Triskelion";

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHero
        eyebrow="About Us · Est. 1982"
        title="A family jeweller on the west coast of Mann"
        sub="Celtic Gold is a family-run jewellery business based in Peel, Isle of Man — a welcoming retail shop combined with an experienced, fully equipped jewellery workshop."
        image={IMG.island}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mb-28">
          <FadeUp>
            <img src={IMG.peelDusk} alt="Peel, Isle of Man" className="w-full aspect-[4/3] object-cover border border-gold/20" />
          </FadeUp>
          <FadeUp delay={0.1}>
            <Chapter number="01" eyebrow="Heritage" title="Rooted in the Island's story" className="mb-8" />
            <p className="text-stone-mist font-light leading-relaxed mb-5">
              Our designs draw inspiration from the Celts and Vikings who settled on the Isle of Man, from
              traditional Manx motifs, and from the work of renowned Manx artist and designer Archibald Knox.
            </p>
            <p className="text-stone-mist font-light leading-relaxed">
              Every collection is carefully created using the lost wax casting process, followed by meticulous hand
              finishing in our Peel workshop — a living connection between the Island's history and the jewellery we
              create today.
            </p>
          </FadeUp>
        </div>

        <div id="team">
          <Chapter number="02" eyebrow="Meet the Celtic Gold Team" title="A small, friendly team" className="mb-4" />
          <FadeUp>
            <p className="text-stone-mist font-light mb-14 max-w-2xl">
              Generations of jewellery knowledge and experience — each member plays a unique and equally important
              role in the running of the shop.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-28">
            {TEAM.map((member, i) => (
              <FadeUp key={member.name + member.role} delay={(i % 3) * 0.08}>
                <div className="border border-white/5 hover:border-gold/40 transition-colors duration-500 bg-ink-2/40 p-8 h-full" data-testid={`team-${member.name.toLowerCase()}-${member.role.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-14 h-14 mb-5 rounded-full border border-gold/40 flex items-center justify-center">
                    <span className="font-serif text-xl text-gold">{member.name.length <= 2 ? member.name : member.name[0]}</span>
                  </div>
                  <h3 className="font-serif text-2xl text-ivory font-light">{member.name}</h3>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold mt-1 mb-4">{member.role}</p>
                  <p className="text-stone-mist text-sm font-light leading-relaxed">{member.bio}</p>
                  {member.note && (
                    <p className="mt-4 text-xs font-mono text-gold border border-gold/25 bg-gold/5 px-3 py-2 inline-block">{member.note}</p>
                  )}
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        <div id="jewellers">
          <Chapter number="03" eyebrow="Our Jewellers" title="Meet the hands behind the jewellery" className="mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <FadeUp>
              <p className="text-stone-mist font-light leading-relaxed mb-5">
                Celtic Gold is proud to have two skilled jewellers, each bringing different experiences and expertise
                to the business. Both work part-time while balancing professional and family commitments — and we
                strive to always have a jeweller available on site to assist customers and continue the
                craftsmanship behind our jewellery.
              </p>
              <div className="flex items-center gap-6 mt-8">
                {["Design", "Craft", "Finish", "Final Piece"].map((step, i) => (
                  <div key={step} className="flex items-center gap-6">
                    <span className="font-serif text-lg text-ivory">{step}</span>
                    {i < 3 && <span className="h-px w-6 bg-gold/40" />}
                  </div>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="grid grid-cols-2 gap-4">
                <img src={IMG.workshop} alt="Jeweller at the bench" className="w-full aspect-square object-cover border border-white/5" />
                <img src={IMG.carving} alt="Wax carving" className="w-full aspect-square object-cover border border-white/5 mt-8" />
              </div>
            </FadeUp>
          </div>
        </div>

        <FadeUp className="mt-28">
          <div className="border border-gold/20 bg-ink-2/60 p-10 text-center">
            <Triskelion className="w-10 h-10 text-gold mx-auto mb-6" strokeWidth={8} />
            <p className="font-serif text-2xl text-ivory font-light mb-2">Visit us in Peel</p>
            <p className="text-stone-mist text-sm">{CONTACT.address.join(", ")} · {CONTACT.phone}</p>
          </div>
        </FadeUp>
      </section>
    </main>
  );
}
